/*
 *   File : header.js
 *   Author : https://evoqins.com
 *   Description : header component
 *   Integrations : auth/profile, notification/count
 *   Version : 1.0.0
 */

import React, { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { PrimaryButton } from "../Buttons";
import { CustomSearchInput } from "../FormElements";
import { useDispatch, useSelector } from "react-redux";
import { setSearchQuery } from "../../Redux/Ui/ui.slice";
import Icon from "../icons";
import { ChangePasswordModal } from "../Modals";
import { APIService } from "../../Service";

/**
 * Header — robust portal overlay + dropdown handling to ensure onClick handlers (logout) run reliably.
 */
export default function Header({ handleToggle, openOffcanvas }) {
  const [menuOpenDrop, setMenuOpenDrop] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [openChangePass, setOpenChangePass] = useState(false);
  const containerRef = useRef(null); // portal wrapper ref
  const overlayRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const navigate = useNavigate();
  const [notificationCount, setNotificationCount] = useState(0);
  const [profile, setProfile] = useState(null);
  const [imageError, setImageError] = useState(false);

  // redux
  const dispatch = useDispatch();
  const placeholder = useSelector((state) => state.ui.placeHolder);
  const title = useSelector((state) => state.ui.pageTitle);
  const searchEnabled = useSelector((state) => state.ui.searchEnabled);
  const searchOnEnter = useSelector((state) => state.ui.searchOnEnter);
  const navLink = useSelector((state) => state.ui.navLink);
  const searchQuery = useSelector((state) => state.ui.searchQuery);

  useEffect(() => {
    if (searchQuery === "") {
      setSearchValue("");
    }
  }, [searchQuery]);
  useEffect(() => {
    _getNotificationCount();
    _getProfile();
  }, []);

  const _getProfile = () => {
    APIService(false, "auth/profile")
      .then((res) => {
        if (res?.status_code === 200) {
          setProfile(res?.data ?? null);
        }
      })
      .catch((err) => {
        console.error("Profile error:", err);
      });
  };

  const profileName = profile?.username
    ? profile.username.charAt(0).toUpperCase() + profile.username.slice(1)
    : "";

  // Initials: first letter of first + last word
  const profileInitials = profileName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase())
    .filter((_, index, arr) => index === 0 || index === arr.length - 1)
    .join("");

  // Reset image error when a new image URL arrives
  useEffect(() => {
    setImageError(false);
  }, [profile?.image]);

  const _renderAvatar = (extraClass = "", onClick) =>
    profile?.image && !imageError ? (
      <img
        onClick={onClick}
        src={profile.image}
        onError={() => setImageError(true)}
        className={`w-32 rounded-circle ${extraClass}`}
        style={{ height: "32px", objectFit: "cover" }}
        alt={profileName || "avatar"}
      />
    ) : (
      <div
        onClick={onClick}
        className={`rounded-circle bg-lavender-blue text-facebook-blue fw-500 fs-12 d-flex align-items-center justify-content-center flex-shrink-0 ${extraClass}`}
        style={{ width: "32px", height: "32px" }}
        title={profileName}
      >
        {profileInitials}
      </div>
    );

  const _getNotificationCount = () => {
    APIService(false, "notification/count")
      .then((res) => {
        if (res?.status_code === 200) {
          setNotificationCount(res?.data?.count ?? 0);
        }
      })
      .catch((err) => {
        console.error("Notification count error:", err);
      });
  };

  // sidebar toggle
  const handleSidebarClick = () => {
    if (window.innerWidth < 1200) openOffcanvas();
    else handleToggle((p) => !p);
  };

  useEffect(() => {
    if (searchQuery === "") {
      setSearchValue("");
    }
  }, [searchQuery]);

  // compute dropdown position when menu opens
  const menuButtonRef = useRef(null);
  useEffect(() => {
    if (!menuOpenDrop) return;
    const updatePosition = () => {
      const btn = menuButtonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [menuOpenDrop]);

  // outside click fallback (optional). Keeps dropdown closed if clicked outside portal area.
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") setMenuOpenDrop(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  // logout handler: perform logout (cookie remove + navigate) BEFORE closing the dropdown to avoid race.
  // Use same path/domain as login so the cookie is removed in production (cookie was set with REACT_APP_DOMAIN).
  const handleLogout = () => {
    try {
      const removeOptions = { path: "/" };
      if (process.env.REACT_APP_DOMAIN) {
        removeOptions.domain = process.env.REACT_APP_DOMAIN;
      }
      Cookies.remove("access_token", removeOptions);
      navigate("/login", { replace: true });
    } finally {
      setMenuOpenDrop(false);
    }
  };

  // Body + dropdown portal content (single portal node so stacking/events are controlled)
  const portalContent = menuOpenDrop ? (
    <div ref={containerRef}>
      {/* Overlay: only closes when you click the overlay itself (not when clicking children) */}
      <div
        ref={overlayRef}
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{ background: "rgba(0,0,0,0.4)", zIndex: 1040 }}
        onMouseDown={(e) => {
          // close only if the overlay itself was clicked (not a child)
          if (e.target === overlayRef.current) {
            setMenuOpenDrop(false);
          }
        }}
      />

      {/* Dropdown: positioned above overlay; stops propagation on both mouseDown and click */}
      {dropdownPosition && (
        <div
          ref={dropdownRef}
          className="e-drop-menu overflow-hidden bg-opacity-43 w-220 e-bg-blur-20 rounded-16 d-flex flex-column"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            position: "fixed",
            top: dropdownPosition.top,
            right: dropdownPosition.right,
            zIndex: 2000, // well above overlay
            pointerEvents: "auto",
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {/* User Info */}
          <div className="d-flex column-gap-2 px-24 py-3 border-bottom border-1 border-white">
            {_renderAvatar()}
            <div className="d-flex flex-column row-gap-2">
              <h5 className="mb-0 fw-500 fs-12 lh-16 text-eerie-black">
                {profileName}
              </h5>
              <p className="mb-0 fw-400 fs-10 lh-1 text-davys-gray">
                {profile?.role}
              </p>
            </div>
          </div>

          {/* Actions — use buttons, keep logic simple */}
          <button
            className="bg-transparent border-0 text-start px-24 py-3 fs-12 fw-400 lh-14 text-eerie-black e-transition-bg-btn"
            onClick={() => setOpenChangePass(true)}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Icon icon="shield" size="16" className="me-2" />
            Change Password
          </button>

          <button
            className="bg-transparent border-0 text-start px-24 py-3 fs-12 fw-400 lh-14 text-eerie-black e-transition-bg-btn"
            onClick={handleLogout}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Icon icon="logout" size="16" className="me-2" />
            Logout
          </button>
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
      {/* Render portal content together into body when menuOpenDrop true */}
      {menuOpenDrop &&
        typeof document !== "undefined" &&
        createPortal(portalContent, document.body)}
      <div
        className={`d-flex ${searchEnabled ? " flex-md-row flex-column align-items-md-center" : "flex-row align-items-center"} row-gap-2 justify-content-between  position-relative z-2 px-16 py-12`}
        style={{ zIndex: menuOpenDrop ? 1051 : undefined }}
      >
        {/* Left: sidebar toggle + title */}
        <div className="d-flex column-gap-3">
          {/* Visible on all sizes: opens offcanvas below xl, collapses/expands sidebar on xl+ */}
          <div className="d-flex align-items-center">
            <img
              onClick={handleSidebarClick}
              className="e-cursor-pointer"
              src={require("../../Assets/Images/Components/ham.png")}
              alt="Toggle sidebar"
              title="Toggle sidebar"
              width={24}
            />
          </div>

          {Array.isArray(title) && title.length > 1 ? (
            <h2 className="fs-14 lh-24 fw-500 text-eerie-black mb-0 text-capitalize">
              {title.map((item, index) => (
                <React.Fragment key={index}>
                  {index < title.length - 1 ? (
                    <Link to={navLink[index]} className="text-decoration-none">
                      <span className="text-davys-gray text-capitalize">
                        {item.toString()} /{" "}
                      </span>
                    </Link>
                  ) : (
                    item.toString()
                  )}
                </React.Fragment>
              ))}
            </h2>
          ) : (
            <h1 className="fs-24 lh-36 fw-500 text-eerie-black mb-0 text-capitalize">
              {title}
            </h1>
          )}
        </div>

        {/* Right: search, notifications, avatar */}
        <div
          className="e-glass-card bg-transparent px-8 py-4x d-flex column-gap-3 align-items-center justify-content-md-start justify-content-between rounded-pill position-relative"
          ref={menuButtonRef}
        >
          {searchEnabled && (
            <CustomSearchInput
              type="text"
              id="search"
              placeholder={placeholder}
              className="w-md-328"
              value={searchValue}
              onEnter={searchOnEnter}
              handleChange={(e) => {
                setSearchValue(e);
                if (!searchOnEnter) {
                  dispatch(setSearchQuery(e));
                } else if (e === "") {
                  dispatch(setSearchQuery(""));
                }
              }}
              onKeyPress={(value) => {
                const query = (value ?? searchValue).trim();
                setSearchValue(query);
                dispatch(setSearchQuery(query));
              }}
            />
          )}

          <div className="d-flex column-gap-2">
            <div className="d-flex position-relative">
              <PrimaryButton
                ClassName="bg-transparent p-1"
                icon="notification"
                size="24px"
                onPress={() => navigate("/notifications")}
              />
              {notificationCount > 0 && (
                <div className="bg-ruby-red e-notification-circle position-absolute d-flex align-items-center">
                  <span className="text-white fs-8 fw-700">
                    {notificationCount > 99 ? "99+" : notificationCount}
                  </span>
                </div>
              )}
            </div>

            {/* Avatar toggles menu open/close */}
            {_renderAvatar("e-cursor-pointer", () =>
              setMenuOpenDrop((p) => !p),
            )}
          </div>
        </div>
      </div>
      <ChangePasswordModal
        open={openChangePass}
        onCancel={() => setOpenChangePass(false)}
        onSuccess={() => console.log("Password Updated!")}
      />
    </>
  );
}
